﻿import { Observable, Subject, AsyncSubject } from 'common/barrels/rx';
import { Event, RequestEvent, RequestWrapper } from 'common/event';
import { IDisposable } from 'common/disposable';
import { Map } from 'immutable';

export interface IEventAggregator extends IDisposable {
    query<T>(type: { new (...args: any[]): T; eventName(): string }): Observable<T>;

    publish(event: { eventName(): string }): any;

    request<TEvent, TResponse>(event: RequestEvent<TEvent, TResponse>): Observable<TResponse>;

    queryRequest<TEvent extends Event, TResponse>(type: { new (...args: any[]): RequestEvent<TEvent, TResponse>; eventName(): string }): Observable<RequestWrapper<TEvent, TResponse>>;
}

export class EventAggregator implements IEventAggregator {
    private subscriptions: Map<string, Subject<any>>;
    private disposed = false;
    private passthroughRegistrations: Map<string, IEventAggregator>;

    constructor() {
        this.subscriptions = Map<string, Subject<any>>();
        this.passthroughRegistrations = null;
    }

    query<T>(type: { new (...args: any[]): T; eventName(): string }): Observable<T> {
        return this.queryInternal<T>(type.eventName());
    }

    private queryInternal<T>(eventName: string) {
        this.errorIfDisposed();

        if (!this.subscriptions.has(eventName))
            this.subscriptions = this.subscriptions.set(eventName, new Subject<any>());

        const subject = this.subscriptions.get(eventName);

        return subject.asObservable();
    }

    publish(event: { eventName(): string }): { eventName(): string } {
        this.errorIfDisposed();

        const eventName = event.eventName();

        if (this.passthroughRegistrations !== null) {
            const reg = this.passthroughRegistrations.get(eventName);
            if (reg)
                return reg.publish(event);
        }

        if (!this.subscriptions.has(eventName))
            return event;

        const subject = this.subscriptions.get(eventName);
        subject.next(event);

        return event;
    }

    errorIfDisposed() {
        if (this.disposed)
            throw new Error('This event aggregator has been disposed');
    }

    dispose() {
        if (this.disposed)
            return;

        this.disposed = true;

        this.subscriptions.forEach(x => x.unsubscribe());

        this.subscriptions = null;
    }

    request<TEvent, TResponse>(event: RequestEvent<TEvent, TResponse>): Observable<TResponse> {
        if (this.passthroughRegistrations !== null && this.passthroughRegistrations.has(event.eventName())) {
            return this.passthroughRegistrations.get(event.eventName()).request(event);
        }

        const s = event.streaming ? new Subject<TResponse>() : new AsyncSubject<TResponse>();

        let completed = false;
        let errored = false;

        const success = (x: TResponse) => {
            completed = true;

            s.next(x);
            s.complete();
        };

        const error = (x: Error) => {
            errored = true;

            s.error(x);
            s.complete();
        }

        const cancel = new AsyncSubject<any>();
        const wrapper = new RequestWrapper(event, success, error, cancel, s);

        this.publish(wrapper);

        return Observable.using(() => {
            return {
                unsubscribe: () => {
                    if (!completed && !errored) {
                        cancel.next({});
                        cancel.complete();
                    }
                }
            };
        }, () => s.asObservable());
    }    

    queryRequest<TEvent extends Event, TResponse>(type: { new (...args: any[]): RequestEvent<TEvent, TResponse>; eventName(): string }): Observable<RequestWrapper<TEvent, TResponse>> {
        return this.queryInternal(RequestWrapper.eventName(type.eventName()));
    }

    passthrough(target: IEventAggregator, ...events: { new (...args: any[]): any; eventName(): string }[]) {
        if (this.passthroughRegistrations === null)
            this.passthroughRegistrations = Map<string, IEventAggregator>();

        // currently the request and non-request things are not separated out, and things are working ok. Need to consider further and make a final decision
        // reasons for separation: name clash, tiny performance difference
        events.forEach(x => this.passthroughRegistrations = this.passthroughRegistrations.set(x.eventName(), target));

        //events.filter(x => !(<any>x).IsRequest).forEach(x => {
        //    this.passthroughRegistrations = this.passthroughRegistrations.set(x.eventName(), target);
        //});

        //events.filter(x => (<any>x).IsRequest).forEach(x => {
        //    this.passthroughRegistrations = this.passthroughRegistrations.set(x.eventName(), target);
        //});
    }
}